import { PropsWithChildren, createContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { getHotelById } from '../apis/hotelApis'
import { IHotelInfo } from '../types/hotelTypes'
import { useQuery } from '@tanstack/react-query'
import { OverlayLoader } from '../components/Loader'
import { Booking } from '../types/booking'
import { JwtPayload, jwtDecode } from 'jwt-decode'
import authClient from '../apis/authClient'
import { useIdleTimer } from 'react-idle-timer'
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material'
import { environment } from '../environments/environment'
import { getKioskDeviceId } from '../utils/deviceUtils'

export interface IHotelContext {
  hotel: IHotelInfo
  reservation: Booking
  setReservationToken: (token: string) => Promise<void>
  clearReservationToken: () => void
  pauseSession: () => void
  resumeSession: () => void
  setIdentity: (id: string) => void
}

const IDLE_TIMEOUT_MS = 90000
const IDLE_PROMPT_MS = 15000

export const HotelContext = createContext<IHotelContext>(null)

export const HotelContextProvider = ({ children }: PropsWithChildren) => {
  const { hotelId } = useParams()
  const [reservation, setReservation] = useState<Booking>(null)
  const location = useLocation()
  const [remaining, setRemaining] = useState<number>(0)
  const [isPrompting, setIsPrompting] = useState(false)
  const navigate = useNavigate()

  console.log(remaining)

  const onIdle = () => {
    setIsPrompting(false)
    clearReservationToken()
    navigate(`/hotel/${hotelId}`)
  }

  const onActive = () => {
    setIsPrompting(false)
  }

  const onPrompt = async () => {
    setRemaining(Math.ceil(getRemainingTime() / 1000))
    setIsPrompting(true)
  }

  const { getRemainingTime, start, reset, pause } = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    promptBeforeIdle: IDLE_PROMPT_MS,
    timeout: IDLE_TIMEOUT_MS,
    throttle: 500,
    startManually: true,
    stopOnIdle: true,
  })

  useEffect(() => {
    if (!environment.kiosk) {
      return
    }

    if (location.pathname.startsWith(`/hotel/${hotelId}/`)) {
      setIsPrompting(false)
      start()
    } else {
      reset()
      pause()
    }
  }, [location.pathname])

  useEffect(() => {
    const deviceId = getKioskDeviceId(location.search)
    if (!deviceId) {
      return
    }

    localStorage.setItem('deviceId', deviceId)
  }, [location.search])

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000))
    }, 500)

    return () => {
      clearInterval(interval)
    }
  }, [isPrompting])

  useMemo(() => {
    const persistedToken = sessionStorage.getItem('token')
    if (persistedToken) {
      try {
        const { exp, ...payload } = jwtDecode(persistedToken) as JwtPayload & Booking
        if (exp * 1000 < new Date().getTime() || hotelId?.toString() !== payload?.hotelId.toString()) {
          sessionStorage.removeItem('token')
          return
        }

        setReservation(payload)
        authClient.defaults.headers['Authorization'] = `Bearer ${persistedToken}`
      } catch (err) {
        console.log(err)
      }
    }
  }, [hotelId])

  const { data: hotel } = useQuery({
    queryKey: [`/api/hotels/${hotelId}`],
    queryFn: () => getHotelById(hotelId),
    enabled: !!hotelId,
  })

  const setReservationToken = async (token: string) => {
    const payload = jwtDecode(token) as Booking
    sessionStorage.setItem('token', token)
    setReservation(payload)
    authClient.defaults.headers['Authorization'] = `Bearer ${token}`
  }

  const clearReservationToken = async () => {
    sessionStorage.removeItem('token')
    setReservation(null)
    authClient.defaults.headers['Authorization'] = null
  }

  const handleClose = () => {
    start()
    setIsPrompting(false)
  }

  const pauseSession = () => {
    setIsPrompting(false)
    reset()
    pause()
  }

  const resumeSession = () => {
    setIsPrompting(false)
    start()
  }

  const setIdentity = (id: string) => {
    setReservation((prev) => ({ ...prev, ezcloud: { ...prev.ezcloud, identityNumber: id } }))
  }

  if (!hotel) {
    return <OverlayLoader />
  }

  return (
    <HotelContext.Provider
      value={{
        hotel,
        setReservationToken,
        clearReservationToken,
        reservation,
        pauseSession,
        resumeSession,
        setIdentity,
      }}>
      {children}
      <Dialog open={isPrompting} onClose={handleClose}>
        <DialogTitle>{'Bạn có đang còn ở đây không?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Hệ thống sẽ tự động kết thúc sau {remaining}s...
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} autoFocus>
            Tiếp tục
          </Button>
        </DialogActions>
      </Dialog>
    </HotelContext.Provider>
  )
}
